import React from 'react';
import { motion } from 'framer-motion';
import { Store, MapPin, Phone, Mail, ShieldCheck, AlertCircle, ChevronRight, Edit, FileText, Star, Package, ShoppingBag } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorProfileScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    store,
    stats,
    products,
    orders
  } = useVendor();
  // Mock KYC status based on taxId existence
  const kycStatus = store.businessInfo.taxId ? 'Verified' : 'Pending';
  return <div className="min-h-full bg-gray-50 dark:bg-gray-900 pb-24" data-id="element-2222">
      <Header title="Seller Profile" data-id="element-2223" />

      <div className="bg-white dark:bg-gray-800 p-6 border-b border-gray-100 dark:border-gray-700" data-id="element-2224">
        <div className="flex items-start gap-4" data-id="element-2225">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2226">
            {store.logo ? <img src={store.logo} alt={store.name} className="w-full h-full rounded-full object-cover" data-id="element-2227" /> : <Store className="w-10 h-10 text-primary" data-id="element-2228" />}
          </div>
          <div className="flex-1" data-id="element-2229">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2230">
              {store.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2231">
              {store.businessInfo.businessName}
            </p>
            <div className="flex items-center gap-2 mt-2" data-id="element-2232">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${kycStatus === 'Verified' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`} data-id="element-2233">
                {kycStatus === 'Verified' ? <ShieldCheck className="w-3 h-3" data-id="element-2234" /> : <AlertCircle className="w-3 h-3" data-id="element-2235" />}
                KYC {kycStatus}
              </span>
              <span className="text-xs text-gray-400" data-id="element-2236">Joined Jan 2024</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6" data-id="element-2237">
          <Button variant="outline" className="flex-1 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" onClick={() => navigate(Screen.VENDOR_STORE_SETTINGS)} data-id="element-2238">
            <Edit className="w-4 h-4 mr-2" data-id="element-2239" /> Edit Profile
          </Button>
          <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" onClick={() => navigate(kycStatus === 'Verified' ? Screen.VENDOR_KYC_EDIT : Screen.VENDOR_KYC)} data-id="element-2240">
            <FileText className="w-4 h-4 mr-2" data-id="element-2241" />{' '}
            {kycStatus === 'Verified' ? 'View KYC' : 'Update KYC'}
          </Button>
        </div>
        <button onClick={() => navigate(Screen.VENDOR_KYC_STATUS)} className="w-full mt-3 text-sm font-medium text-primary text-center py-2" data-id="element-2242">
          View KYC Verification Status →
        </button>
      </div>

      <div className="p-4" data-id="element-2243">
        <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-2244">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl text-center shadow-sm" data-id="element-2245">
            <Package className="w-6 h-6 text-primary mx-auto mb-2" data-id="element-2246" />
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2247">
              {products.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2248">
              Products
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl text-center shadow-sm" data-id="element-2249">
            <ShoppingBag className="w-6 h-6 text-accent mx-auto mb-2" data-id="element-2250" />
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2251">
              {orders.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2252">
              Orders
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl text-center shadow-sm" data-id="element-2253">
            <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" data-id="element-2254" />
            <div className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2255">
              {store.rating}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2256">
              Rating
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden" data-id="element-2257">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700" data-id="element-2258">
            <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-2259">
              Contact Information
            </h3>
          </div>
          <div className="p-4 space-y-4" data-id="element-2260">
            <div className="flex items-center gap-3" data-id="element-2261">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center" data-id="element-2262">
                <Mail className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2263" />
              </div>
              <div data-id="element-2264">
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2265">
                  Email Address
                </div>
                <div className="font-medium text-gray-900 dark:text-white" data-id="element-2266">
                  contact@{store.name.toLowerCase().replace(/\s/g, '')}.com
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-2267">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center" data-id="element-2268">
                <Phone className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2269" />
              </div>
              <div data-id="element-2270">
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2271">
                  Phone Number
                </div>
                <div className="font-medium text-gray-900 dark:text-white" data-id="element-2272">
                  +234 800 123 4567
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-2273">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 flex items-center justify-center" data-id="element-2274">
                <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-2275" />
              </div>
              <div data-id="element-2276">
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2277">
                  Business Address
                </div>
                <div className="font-medium text-gray-900 dark:text-white" data-id="element-2278">
                  {store.businessInfo.businessAddress}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}